// Host View (Phase 2) - IMPROVED VERSION
// Handles host/greeter panel logic and queue management actions

import {
    setPartySize,
    togglePreferential,
    getSellerStatus,
    getSellerDetails,
    calculateTimeBreakdown,
    formatTime,
    getQueueStats,
    assignCustomerToSeller,
    unassignCustomer
} from '../services/host-service.js';
import { getSellerInfo, startShift, endShift, togglePause } from '../services/seller-service.js';
import { calculateWaitTime } from '../utils/time-calculator.js';
import { STATUS, SELLER_STATUS, HOST_COLORS, FILTER_TYPES } from '../config/constants.js';
import stateManager from '../core/state-manager.js';
import { logout } from '../core/auth-manager.js';

// Current filter (waiting or standby)
let currentFilter = FILTER_TYPES.WAITING;

/**
 * Initialize host view controls
 */
export function initHostViewControls() {
    // Shift control
    document.getElementById('host-shift-btn').addEventListener('click', handleShiftToggle);

    // Pause control
    document.getElementById('host-pause-btn')?.addEventListener('click', handlePauseToggle);

    // Logout
    document.getElementById('host-logout-btn').addEventListener('click', logout);

    // Queue action delegation
    document.getElementById('host-queue-list').addEventListener('click', handleQueueAction);

    // Filter clicks - all stat cards are clickable
    document.getElementById('host-filter-waiting')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.WAITING;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        const sellers = stateManager.getState('sellersData');
        updateCustomerQueueList(allCustomers, sellers);
    });

    document.getElementById('host-filter-serving')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.SERVING;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        const sellers = stateManager.getState('sellersData');
        updateCustomerQueueList(allCustomers, sellers);
    });

    document.getElementById('host-filter-aforo')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.AFORO;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        const sellers = stateManager.getState('sellersData');
        updateCustomerQueueList(allCustomers, sellers);
    });

    document.getElementById('host-filter-standby')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.STANDBY;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        const sellers = stateManager.getState('sellersData');
        updateCustomerQueueList(allCustomers, sellers);
    });

    document.getElementById('host-filter-done')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.DONE;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        const sellers = stateManager.getState('sellersData');
        updateCustomerQueueList(allCustomers, sellers);
    });
}

/**
 * Update filter UI - highlight active filter
 */
function updateFilterUI() {
    const filters = ['waiting', 'serving', 'aforo', 'standby', 'done'];

    filters.forEach(filterName => {
        const div = document.getElementById(`host-filter-${filterName}`);
        if (!div) return;

        const isActive = currentFilter === FILTER_TYPES[filterName.toUpperCase()];

        if (isActive) {
            // Add thick border to active filter
            div.classList.add('ring-4', 'ring-offset-2');
            const colorMap = {
                'waiting': 'ring-blue-500',
                'serving': 'ring-green-500',
                'aforo': 'ring-purple-500',
                'standby': 'ring-gray-400',
                'done': 'ring-teal-500'
            };
            div.classList.add(colorMap[filterName]);
        } else {
            // Remove ring from inactive filters
            div.classList.remove('ring-4', 'ring-offset-2', 'ring-green-500', 'ring-blue-500', 'ring-purple-500', 'ring-gray-400', 'ring-teal-500');
        }
    });
}

/**
 * Handle shift toggle (start/end)
 */
async function handleShiftToggle() {
    const currentUser = stateManager.getState('currentUser');
    const isShiftActive = stateManager.getState('isShiftActive');

    if (!currentUser) return;

    if (isShiftActive) {
        // End shift and logout
        await endShift(currentUser.uid);
        await logout();
    } else {
        // Start shift
        await startShift(currentUser.uid);
        stateManager.setShiftActive(true);
        updateShiftAndPauseButtonsState();

        // Refresh view
        const allCustomers = stateManager.getState('allCustomers');
        updateHostView(allCustomers);
    }
}

/**
 * Handle pause toggle
 */
async function handlePauseToggle() {
    const currentUser = stateManager.getState('currentUser');
    const isSellerPaused = stateManager.getState('isSellerPaused');

    if (!currentUser) return;

    const newPauseState = !isSellerPaused;
    await togglePause(currentUser.uid, newPauseState);

    stateManager.setSellerPaused(newPauseState);
    updateShiftAndPauseButtonsState();

    // Refresh view
    const allCustomers = stateManager.getState('allCustomers');
    updateHostView(allCustomers);
}

/**
 * Update shift and pause button states
 */
function updateShiftAndPauseButtonsState() {
    const shiftBtn = document.getElementById('host-shift-btn');
    const pauseBtn = document.getElementById('host-pause-btn');
    const isShiftActive = stateManager.getState('isShiftActive');
    const isSellerPaused = stateManager.getState('isSellerPaused');

    if (isShiftActive) {
        // Shift is active - show "Terminar Jornada"
        shiftBtn.innerHTML = '<span class="text-lg">🏁</span><span class="text-sm">Terminar Jornada</span>';
        shiftBtn.className = "w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
        shiftBtn.style.minHeight = '48px';

        if (pauseBtn) {
            pauseBtn.style.display = 'flex';
            if (isSellerPaused) {
                // Paused - show "Reanudar" (Resume)
                pauseBtn.innerHTML = '<span class="text-lg">▶️</span><span class="text-sm">Reanudar</span>';
                pauseBtn.className = "w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
                pauseBtn.style.minHeight = '48px';
            } else {
                // Active - show "Pausar"
                pauseBtn.innerHTML = '<span class="text-lg">⏸️</span><span class="text-sm">Pausar</span>';
                pauseBtn.className = "w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
                pauseBtn.style.minHeight = '48px';
            }
        }
    } else {
        // Shift is not active - show "Iniciar Jornada"
        shiftBtn.innerHTML = '<span class="text-lg">▶️</span><span class="text-sm">Iniciar Jornada</span>';
        shiftBtn.className = "w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
        shiftBtn.style.minHeight = '48px';
        if (pauseBtn) {
            pauseBtn.style.display = 'none';
        }
    }
}

/**
 * Handle queue action clicks (delegation pattern)
 */
async function handleQueueAction(e) {
    const btn = e.target.closest('button[data-action]');
    if (!btn) return;

    const customerId = btn.dataset.customerId;
    const action = btn.dataset.action;

    try {
        switch (action) {
            case 'toggle-preferential':
                await handleTogglePreferential(customerId);
                break;
            case 'set-party-size':
                await handleSetPartySize(customerId);
                break;
            case 'assign-customer':
                await handleAssignCustomer(customerId);
                break;
            case 'unassign-customer':
                await handleUnassignCustomer(customerId);
                break;
            case 'reactivate-customer':
                await handleReactivateCustomer(customerId);
                break;
        }
    } catch (error) {
        console.error(`Error handling ${action}:`, error);
        alert(`Error: ${error.message}`);
    }
}

/**
 * Reactivate customer from standby
 */
async function handleReactivateCustomer(customerId) {
    const { reactivateCustomer } = await import('../services/queue-service.js');
    await reactivateCustomer(customerId);
}

/**
 * Toggle preferential status
 */
async function handleTogglePreferential(customerId) {
    const allCustomers = stateManager.getState('allCustomers');
    const customer = allCustomers.find(c => c.id === customerId);

    if (!customer) return;

    const currentUser = stateManager.getState('currentUser');
    const newStatus = !customer.isPreferential;

    await togglePreferential(customerId, newStatus, currentUser.email);
}

/**
 * Set party size
 */
async function handleSetPartySize(customerId) {
    const allCustomers = stateManager.getState('allCustomers');
    const customer = allCustomers.find(c => c.id === customerId);

    if (!customer) return;

    const currentSize = customer.partySize || 1;
    const newSize = prompt(`Tamaño del grupo actual: ${currentSize}\nIngresa nuevo tamaño (1-10):`, currentSize);

    if (newSize && parseInt(newSize) > 0 && parseInt(newSize) <= 10) {
        await setPartySize(customerId, parseInt(newSize));
    }
}

/**
 * Assign customer to a seller
 */
async function handleAssignCustomer(customerId) {
    const sellers = stateManager.getState('sellersData');
    const allCustomers = stateManager.getState('allCustomers');
    const currentUser = stateManager.getState('currentUser');

    // Fix: Check if sellers is null/undefined
    if (!sellers || typeof sellers !== 'object') {
        alert('No se pueden cargar las vendedoras. Intenta refrescar la página.');
        return;
    }

    // Get available sellers
    const availableSellers = Object.values(sellers).filter(seller => {
        const status = getSellerStatus(seller, allCustomers);
        return status === SELLER_STATUS.AVAILABLE;
    });

    if (availableSellers.length === 0) {
        alert('No hay vendedoras disponibles en este momento.');
        return;
    }

    // Build selection UI
    const sellerOptions = availableSellers.map((s, i) => `${i + 1}. ${s.name || s.email}`).join('\n');
    const selection = prompt(`Selecciona vendedora:\n${sellerOptions}\n\n¿Usar pre-serving? (s/n)`, '1');

    if (!selection) return;

    const parts = selection.toLowerCase().split(' ');
    const sellerIndex = parseInt(parts[0]) - 1;
    const usePreServing = parts.length > 1 && parts[1] === 's';

    if (sellerIndex >= 0 && sellerIndex < availableSellers.length) {
        const selectedSeller = availableSellers[sellerIndex];
        await assignCustomerToSeller(customerId, selectedSeller.email, currentUser.email, usePreServing);
    }
}

/**
 * Unassign customer
 */
async function handleUnassignCustomer(customerId) {
    if (confirm('¿Quitar asignación de este cliente?')) {
        await unassignCustomer(customerId);
    }
}

/**
 * Main update function for host view
 */
export function updateHostView(allCustomers) {
    const currentUser = stateManager.getState('currentUser');
    const sellers = stateManager.getState('sellersData');
    const isShiftActive = stateManager.getState('isShiftActive');

    if (!currentUser) return;

    // Update email display
    document.getElementById('host-email').textContent = currentUser.email;

    // Update shift and pause buttons
    updateShiftAndPauseButtonsState();

    // Update stats
    const stats = getQueueStats(allCustomers);
    document.getElementById('host-waiting-count').textContent = stats.waiting;
    document.getElementById('host-serving-count').textContent = stats.serving;

    // Update Total / Aforo card with dual KPIs
    const activeCustomersEl = document.getElementById('host-active-customers-count');
    if (activeCustomersEl) {
        activeCustomersEl.textContent = stats.totalActiveCustomers;
    }
    document.getElementById('host-people-count').textContent = stats.totalPeople;

    // Update standby count if filter exists
    const standbyCountEl = document.getElementById('host-standby-count');
    if (standbyCountEl) {
        standbyCountEl.textContent = stats.standby;
    }

    // Update done count if filter exists
    const doneCountEl = document.getElementById('host-done-count');
    if (doneCountEl) {
        doneCountEl.textContent = stats.done;
    }

    // Update customer queue list
    updateCustomerQueueList(allCustomers, sellers);

    // Update seller availability list
    updateSellersList(sellers, allCustomers);
}

/**
 * Update customer queue list
 */
function updateCustomerQueueList(allCustomers, sellers) {
    const queueList = document.getElementById('host-queue-list');

    // Filter based on current filter
    let filteredCustomers;
    switch (currentFilter) {
        case FILTER_TYPES.WAITING:
            // Show only waiting + calling (NOT serving)
            filteredCustomers = allCustomers.filter(c =>
                [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING].includes(c.status)
            );
            break;
        case FILTER_TYPES.SERVING:
            // Show only serving customers
            filteredCustomers = allCustomers.filter(c => c.status === STATUS.SERVING);
            break;
        case FILTER_TYPES.AFORO:
            // Show all active: waiting + calling + serving
            filteredCustomers = allCustomers.filter(c =>
                [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING, STATUS.SERVING].includes(c.status)
            );
            break;
        case FILTER_TYPES.STANDBY:
            // Show standby customers only
            filteredCustomers = allCustomers.filter(c => c.status === STATUS.STANDBY);
            break;
        case FILTER_TYPES.DONE:
            // Show completed customers from TODAY only
            filteredCustomers = allCustomers.filter(c => {
                if (c.status !== STATUS.DONE) return false;
                if (!c.finishedAt) return false;

                const finishedDate = c.finishedAt.toDate ? c.finishedAt.toDate() : new Date(c.finishedAt);
                const today = new Date();

                return finishedDate.getDate() === today.getDate() &&
                       finishedDate.getMonth() === today.getMonth() &&
                       finishedDate.getFullYear() === today.getFullYear();
            });
            break;
        default:
            // Default to waiting
            filteredCustomers = allCustomers.filter(c =>
                [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING].includes(c.status)
            );
    }

    // Sort: preferential first, then by creation time
    filteredCustomers.sort((a, b) => {
        if (a.isPreferential && !b.isPreferential) return -1;
        if (!a.isPreferential && b.isPreferential) return 1;
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
    });

    if (filteredCustomers.length === 0) {
        queueList.innerHTML = '<div class="text-center text-gray-500 p-8">No hay clientes en esta vista</div>';
        return;
    }

    queueList.innerHTML = filteredCustomers.map((customer, index) => createHostCustomerCard(customer, index + 1, sellers)).join('');
}

/**
 * Format timestamp to HH:MM
 */
function formatTimestamp(timestamp) {
    if (!timestamp) return '--:--';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

/**
 * Create customer card for host view - IMPROVED 2-LINE LAYOUT
 */
function createHostCustomerCard(customer, position, sellers) {
    const timeBreakdown = calculateTimeBreakdown(customer);
    const partySize = customer.partySize || 1;

    // Fix: Handle null sellers
    const assignedSeller = (customer.assignedTo && sellers) ? sellers[customer.assignedTo] : null;

    // Border styling for calling customers and reactivated customers
    let borderClass = customer.isPreferential ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 bg-white';
    let borderStyle = '';

    if (customer.status === STATUS.CALLING) {
        // Green pulse for calling
        borderClass = 'border-green-400 bg-green-50';
        borderStyle = 'border-l-8 border-l-green-500 animate-pulse-border';
    } else if (customer.wasReactivated) {
        // Light blue pulse for reactivated customers
        borderClass = 'border-blue-300 bg-blue-50';
        borderStyle = 'border-l-8 border-l-blue-400 animate-pulse-border-blue';
    }

    // Status badge
    let statusBadge = '';
    let statusClass = '';

    switch (customer.status) {
        case STATUS.WAITING:
            statusBadge = 'Esperando';
            statusClass = 'bg-blue-500 text-white';
            break;
        case STATUS.PRE_SERVING:
            statusBadge = 'Pre-Atención';
            statusClass = 'bg-purple-500 text-white';
            break;
        case STATUS.CALLING:
            const callerName = customer.calledByName || (customer.calledBy ? customer.calledBy.split('@')[0] : 'Vendedora');
            statusBadge = `📞 ${callerName}`;
            statusClass = 'bg-green-100 text-green-800';
            break;
        case STATUS.SERVING:
            statusBadge = 'Atendiendo';
            statusClass = 'bg-green-600 text-white';
            break;
        case STATUS.STANDBY:
            statusBadge = 'En Pausa';
            statusClass = 'bg-gray-500 text-white';
            break;
    }

    // Assigned badge
    const assignedBadge = assignedSeller
        ? `<span class="px-1 py-0.5 bg-green-100 text-green-800 text-xs rounded">→ ${assignedSeller.name || assignedSeller.email.split('@')[0]}</span>`
        : '';

    // Get seller name for "Atendido por" - use sellers object if available
    let attendedByName = '';
    if (customer.attendedBy && sellers) {
        const attendedSeller = sellers[customer.attendedBy];
        attendedByName = attendedSeller?.name || customer.attendedByName || customer.attendedBy.split('@')[0];
    } else if (customer.attendedByName) {
        attendedByName = customer.attendedByName;
    } else if (customer.attendedBy) {
        attendedByName = customer.attendedBy.split('@')[0];
    }

    // Time information
    const registrationTime = formatTimestamp(customer.createdAt);
    const waitingTime = formatTime(timeBreakdown.waitingTime);
    // Show serving time for SERVING and DONE customers
    const servingTime = (customer.status === STATUS.SERVING || customer.status === STATUS.DONE)
        ? formatTime(timeBreakdown.servingTime)
        : null;

    // Calculate estimated wait time using the CORRECT algorithm (considers active sellers)
    const sellersData = stateManager.getState('sellersData');
    const estimatedWaitMins = customer.status === STATUS.WAITING
        ? calculateWaitTime(position, sellersData)
        : null;

    return `
        <div class="border rounded-lg p-2 ${borderClass} ${borderStyle} hover:shadow-md transition-shadow">
            <div class="flex justify-between items-center gap-2">
                <div class="flex-1 min-w-0">
                    <!-- LINE 1: Position, Name, Star, Status -->
                    <div class="flex items-center gap-2 mb-0.5">
                        <span class="font-bold text-base">#${position}</span>
                        ${customer.isPreferential ? '<span class="text-base" title="Preferencial">⭐</span>' : ''}
                        <span class="font-semibold text-sm truncate">${customer.firstName} ${customer.lastName}</span>
                        <span class="px-1.5 py-0.5 ${statusClass} text-xs font-semibold rounded whitespace-nowrap">${statusBadge}</span>
                        ${assignedBadge}
                    </div>
                    <!-- LINE 2: Type, Reg Time, Wait Time, Serving Time, Total, Attended By, Est Wait -->
                    <div class="text-xs text-gray-600 flex items-center gap-1 flex-wrap">
                        <span>${customer.attentionType}</span>
                        <span>•</span>
                        <span>📅${registrationTime}</span>
                        <span>•</span>
                        <span>⏱️${waitingTime}</span>
                        ${servingTime ? `<span>•</span><span>👟${servingTime}</span>` : ''}
                        <span>•</span>
                        <span>⏰${formatTime(timeBreakdown.totalTime)}</span>
                        ${attendedByName ? `<span>•</span><span>por:${attendedByName}</span>` : ''}
                        ${estimatedWaitMins ? `<span>•</span><span class="text-orange-600 font-semibold">⏳~${estimatedWaitMins}min</span>` : ''}
                    </div>
                </div>
                <!-- Action Buttons -->
                <div class="flex gap-1 flex-shrink-0">
                    <button data-action="toggle-preferential" data-customer-id="${customer.id}" class="px-1.5 py-1 text-xs ${customer.isPreferential ? 'bg-gray-200 hover:bg-gray-300' : 'bg-yellow-200 hover:bg-yellow-300'} rounded" title="Toggle preferencial">⭐</button>
                    <button data-action="set-party-size" data-customer-id="${customer.id}" class="px-1.5 py-1 text-xs bg-purple-100 hover:bg-purple-200 rounded" title="Tamaño grupo">👥${partySize}</button>
                    ${(customer.status === STATUS.WAITING || customer.status === STATUS.PRE_SERVING) && !customer.assignedTo ? `<button data-action="assign-customer" data-customer-id="${customer.id}" class="px-1.5 py-1 text-xs bg-green-100 hover:bg-green-200 rounded" title="Asignar">→</button>` : ''}
                    ${(customer.status === STATUS.WAITING || customer.status === STATUS.PRE_SERVING) && customer.assignedTo ? `<button data-action="unassign-customer" data-customer-id="${customer.id}" class="px-1.5 py-1 text-xs bg-red-100 hover:bg-red-200 rounded" title="Quitar asignación">✕</button>` : ''}
                    ${customer.status === STATUS.STANDBY ? `<button data-action="reactivate-customer" data-customer-id="${customer.id}" class="px-1.5 py-1 text-xs bg-blue-100 hover:bg-blue-200 rounded" title="Reactivar">↻</button>` : ''}
                </div>
            </div>
        </div>
    `;
}

/**
 * Update sellers availability list
 */
function updateSellersList(sellers, allCustomers) {
    const sellersList = document.getElementById('host-sellers-list');

    // Debug logging
    console.log('[HOST VIEW] updateSellersList called');
    console.log('[HOST VIEW] sellers:', sellers);
    console.log('[HOST VIEW] sellers type:', typeof sellers);
    console.log('[HOST VIEW] sellers keys:', sellers ? Object.keys(sellers) : 'null');

    // Fix: Handle null/undefined sellers
    if (!sellers || typeof sellers !== 'object') {
        console.log('[HOST VIEW] Sellers is null or not an object');
        sellersList.innerHTML = '<div class="text-center text-gray-500 p-4 text-sm">Cargando vendedoras...</div>';
        return;
    }

    const sellersArray = Object.values(sellers);
    console.log('[HOST VIEW] sellersArray:', sellersArray);
    console.log('[HOST VIEW] sellersArray length:', sellersArray.length);

    // Filter to show only logged-in sellers (isOnline = true)
    const onlineSellers = sellersArray.filter(seller => seller.isOnline === true);
    console.log('[HOST VIEW] onlineSellers:', onlineSellers);
    console.log('[HOST VIEW] onlineSellers length:', onlineSellers.length);

    if (onlineSellers.length === 0) {
        console.log('[HOST VIEW] No online sellers');
        sellersList.innerHTML = '<div class="text-center text-gray-500 p-4 text-sm">No hay vendedoras activas</div>';
        return;
    }

    console.log('[HOST VIEW] Creating seller cards for', onlineSellers.length, 'online sellers');
    sellersList.innerHTML = onlineSellers.map(seller => createSellerCard(seller, allCustomers)).join('');
}

/**
 * Create seller availability card
 */
function createSellerCard(seller, allCustomers) {
    const details = getSellerDetails(seller, allCustomers);
    const status = details.status;

    let statusColor = HOST_COLORS.OFFLINE;
    let statusText = 'Offline';
    let statusBgColor = 'bg-gray-200';

    switch (status) {
        case SELLER_STATUS.AVAILABLE:
            statusColor = HOST_COLORS.AVAILABLE;
            statusText = 'Disponible';
            statusBgColor = 'bg-green-100';
            break;
        case SELLER_STATUS.BUSY:
            statusColor = HOST_COLORS.BUSY;
            statusText = 'Ocupada';
            statusBgColor = 'bg-red-100';
            break;
        case SELLER_STATUS.PAUSED:
            statusColor = HOST_COLORS.PAUSED;
            statusText = 'En Pausa';
            statusBgColor = 'bg-yellow-100';
            break;
    }

    const currentCustomerInfo = details.currentCustomer
        ? `<div class="text-xs text-gray-600 mt-1">→ ${details.currentCustomer.firstName} ${details.currentCustomer.lastName}</div>`
        : '';

    return `
        <div class="border-2 rounded-lg p-2 ${statusBgColor}" style="border-color: ${statusColor};">
            <div class="flex items-center justify-between">
                <div class="flex-1">
                    <div class="font-semibold text-sm">${seller.name || seller.email.split('@')[0]}</div>
                    <div class="text-xs font-bold" style="color: ${statusColor};">${statusText}</div>
                    ${currentCustomerInfo}
                </div>
                <div class="w-3 h-3 rounded-full" style="background-color: ${statusColor};"></div>
            </div>
        </div>
    `;
}

/**
 * Load host seller information
 */
export async function loadHostSellerInfo() {
    const currentUser = stateManager.getState('currentUser');

    if (!currentUser) return;

    const sellerInfo = await getSellerInfo(currentUser.uid);

    if (sellerInfo) {
        stateManager.setShiftActive(sellerInfo.isOnline || false);
        stateManager.setSellerPaused(sellerInfo.isPaused || false);
    }
}
